// frontend/src/pages/Settings.tsx
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { LOCAL_STORAGE_LANG_KEY } from "@/i18n";
import { Language, normalizeLanguage, SUPPORTED_LANGUAGES } from "@/locales/locales";
import { getMe, updateMe } from "@/services/users";
import { useAuth } from "@/context/use-auth";
import Button from "@/components/ui/Button";
import { showToast } from "@/utils/toast";


export default function Settings() {
  const { user, setUser } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState<Language>("en");
  const [country, setCountry] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        setFirstName(me.first_name ?? user?.first_name ?? "");
        setLastName(me.last_name ?? user?.last_name ?? "");
        setEmail(me.email ?? user?.email ?? "");
        setLanguage(normalizeLanguage(me.language) ?? "en");
        setCountry(me.country ?? "");
        setCurrency(me.currency ?? "EUR");
      } catch {
        setFirstName(user?.first_name ?? "");
        setLastName(user?.last_name ?? "");
        setEmail(user?.email ?? "");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await updateMe({
        first_name: firstName,
        last_name: lastName,
        language,
        country,
        currency,
      });

      // After a successful update:
      const lang = normalizeLanguage(updated.language) ?? "en";
      await i18n.changeLanguage(lang); // Immediate UI change (no waiting for refetch)
      // Keep HTML lang in sync and persist for next boot
      try {
        document?.documentElement?.setAttribute("lang", lang);
        localStorage.setItem(LOCAL_STORAGE_LANG_KEY, lang);
      } catch { /* ignore */ }
      
      // Optionally refetch profile to keep the auth context aligned with server (if you have a getMe() + setUser or equivalent; adapt to your context)
      try {
        const me = await getMe();
        setUser({ ...(user ?? {}), ...me, language: normalizeLanguage(me.language) });
      } catch { /* ignore */ }

      // Update local state to reflect saved values
      setFirstName(updated.first_name ?? "");
      setLastName(updated.last_name ?? "");
      setLanguage(normalizeLanguage(updated.language) ?? "en");
      setCountry(updated.country ?? "");
      setCurrency(updated.currency ?? "EUR");

      showToast(t("auth.settings.success"), "success");
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || t("auth.settings.error");
      showToast(String(msg), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 px-4 pt-12 pb-8 md:pb-12">
        <div className="mx-auto w-full max-w-2xl bg-white shadow rounded p-6">
          <h1 className="text-xl font-semibold mb-4">{t("auth.settings.title")}</h1>
          <p className="text-sm text-gray-700">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 px-4 pt-12 pb-8 md:pb-12">
      <div className="mx-auto w-full max-w-2xl bg-white shadow rounded p-6">
        <h1 className="text-xl font-semibold mb-4">{t("auth.settings.title")}</h1>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
          {/* Names in two columns on md+ */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.first_name")}</label>
              <input
                className="standard-form-field"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.last_name")}</label>
              <input
                className="standard-form-field"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.email")}</label>
            <input
              className="standard-form-field bg-gray-100 text-gray-700"
              type="email"
              value={email}
              readOnly
              title="Email cannot be changed"
            />
          </div>

          {/* Align with the width of one grid column (same as 'First name') */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.country")}</label>
                <input
                  className="standard-form-field"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  autoComplete="country-name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.language")}</label>
                <select
                  className="standard-form-field bg-white"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as Language)}
                >
                  <option value="en">English</option>
                  <option value="de">Deutsch</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("auth.settings.currency")}</label>
                <select
                  className="standard-form-field bg-white"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option value="EUR">EUR</option>
                </select>
              </div>

            </div>

            {/* Right column intentionally left empty to mirror the two-column grid sizing */}
            <div className="hidden md:block" aria-hidden="true" />
          </div>

          <div className="pt-2">
            <Button type="submit" variant="orange" loading={saving} loadingText={t("common.saving")}>
              {t("actions.save")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
